import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '../../Toast';
import { useConfirm } from '../../../hooks/useConfirm';
import extractedData from '../../../data/extracted_payers_categorized.json';

interface Payer {
  id: string;
  payerId: string;
  name: string;
  address: string;
  claimFilingEnrollment: 'ready' | 'not-supported' | 'pending';
  paymentReportEnrollment: 'ready' | 'not-supported' | 'pending';
}

interface ExtractedPayer {
  payerId: string;
  payerName: string;
  address: string;
  notes?: string;
}

interface BatchIntegrationProps {
  onClose: () => void;
  onComplete: () => void;
}

const BatchPayerIntegration: React.FC<BatchIntegrationProps> = ({ onClose, onComplete }) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [currentBatch, setCurrentBatch] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedBatches, setCompletedBatches] = useState<number[]>([]);
  const [integrationStats, setIntegrationStats] = useState({
    totalAdded: 0,
    duplicatesSkipped: 0
  });

  const batches = [
    {
      id: 1,
      name: 'National Major Insurers',
      key: 'national_major_insurers',
      count: 45,
      description: 'High priority, well-known brands',
      priority: 'High'
    },
    {
      id: 2,
      name: 'Regional BCBS Plans',
      key: 'regional_bcbs_plans',
      count: 89,
      description: 'Essential for nationwide coverage',
      priority: 'High'
    },
    {
      id: 3,
      name: 'Medicare/Medicaid Plans',
      key: 'medicare_medicaid_plans',
      count: 112,
      description: 'Government programs, critical',
      priority: 'Critical'
    },
    {
      id: 4,
      name: 'Behavioral Health Networks',
      key: 'behavioral_health_networks',
      count: 67,
      description: 'Core to mental health specialty',
      priority: 'High'
    },
    {
      id: 5,
      name: 'Managed Care Organizations',
      key: 'managed_care_organizations',
      count: 143,
      description: 'Common in practices',
      priority: 'Medium'
    },
    {
      id: 6,
      name: 'Third Party Administrators',
      key: 'third_party_administrators',
      count: 98,
      description: 'Employer-sponsored plans',
      priority: 'Medium'
    }
  ];

  // Load completed batches from Firestore
  useEffect(() => {
    if (!user) return;
    const loadCompleted = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'batch_payer_integration');
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().completedBatches) {
          setCompletedBatches(snap.data().completedBatches);
        }
      } catch (err) {
        console.error('Error loading batch integration state:', err);
      }
    };
    loadCompleted();
  }, [user, userProfile]);

  const handleIntegrateBatch = async (batchId: number) => {
    setIsProcessing(true);

    const batch = batches.find(b => b.id === batchId);
    if (!batch) return;

    try {
      // Get current payers from Firestore
      const payersDocRef = doc(db, 'billing_settings', 'payers');
      const payersSnap = await getDoc(payersDocRef);
      const currentPayers: Payer[] = payersSnap.exists() && payersSnap.data().payers ? payersSnap.data().payers : [];

      // Get extracted payers for this batch
      const extractedPayers = (extractedData.categories as any)[batch.key] as ExtractedPayer[];

      let added = 0;
      let skipped = 0;

      // Process each payer in the batch
      extractedPayers.forEach((extractedPayer, index) => {
        // Check if payer already exists (by payerId)
        const exists = currentPayers.some(p => p.payerId === extractedPayer.payerId);

        if (!exists) {
          const newPayer: Payer = {
            id: `batch${batchId}_${index}_${Date.now()}`,
            payerId: extractedPayer.payerId,
            name: extractedPayer.payerName,
            address: extractedPayer.address || '',
            claimFilingEnrollment: 'ready',
            paymentReportEnrollment: 'ready'
          };
          currentPayers.push(newPayer);
          added++;
        } else {
          skipped++;
        }
      });

      // Save updated payers to Firestore
      await setDoc(payersDocRef, { payers: currentPayers, ownerId: user?.uid || '', updatedAt: new Date().toISOString() }, { merge: true });

      // Update completed batches
      const updatedCompleted = [...completedBatches, batchId];
      setCompletedBatches(updatedCompleted);

      const batchDocRef = doc(db, 'billing_settings', 'batch_payer_integration');
      await setDoc(batchDocRef, { completedBatches: updatedCompleted, ownerId: user?.uid || '', updatedAt: new Date().toISOString() }, { merge: true });

      // Update stats
      setIntegrationStats({
        totalAdded: integrationStats.totalAdded + added,
        duplicatesSkipped: integrationStats.duplicatesSkipped + skipped
      });

      // Show success message
      toast.success(`Batch ${batchId} integrated successfully! Added: ${added} payers, Skipped (duplicates): ${skipped} payers`);

    } catch (error) {
      console.error('Error integrating batch:', error);
      toast.error('Error integrating batch. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIntegrateAll = async () => {
    if (!await confirm({ title: 'Integrate All Batches', message: 'This will integrate all 6 batches (554 payers). Continue?', confirmLabel: 'Integrate All', variant: 'warning' })) {
      return;
    }

    for (const batch of batches) {
      if (!completedBatches.includes(batch.id)) {
        await handleIntegrateBatch(batch.id);
        // Small delay between batches
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    toast.success('All batches integrated successfully!');
    onComplete();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return '#DC2626';
      case 'High': return '#F59E0B';
      case 'Medium': return '#10B981';
      default: return '#6B7280';
    }
  };

  const allBatchesCompleted = batches.every(b => completedBatches.includes(b.id));

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>
              Batch Payer Integration
            </h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
              Add 554 healthcare payers in 6 organized batches
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#9CA3AF',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px'
            }}
          >
            ×
          </button>
        </div>

        {/* Stats */}
        <div
          style={{
            padding: '24px',
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                Total Available
              </div>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                554
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                Added This Session
              </div>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#10B981' }}>
                {integrationStats.totalAdded}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                Batches Completed
              </div>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#2563EB' }}>
                {completedBatches.length}/6
              </div>
            </div>
          </div>
        </div>

        {/* Batches */}
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              Integration Batches
            </h3>
            <button
              onClick={handleIntegrateAll}
              disabled={isProcessing || allBatchesCompleted}
              style={{
                padding: '8px 16px',
                backgroundColor: allBatchesCompleted ? '#9CA3AF' : '#2563EB',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: allBatchesCompleted ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1
              }}
            >
              {allBatchesCompleted ? '✓ All Completed' : 'Integrate All Batches'}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {batches.map((batch) => {
              const isCompleted = completedBatches.includes(batch.id);

              return (
                <div
                  key={batch.id}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    padding: '16px',
                    backgroundColor: isCompleted ? '#F0FDF4' : '#FFFFFF'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span
                          style={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          Batch {batch.id}
                        </span>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                          {batch.name}
                        </h4>
                        <span
                          style={{
                            backgroundColor: `${getPriorityColor(batch.priority)}15`,
                            color: getPriorityColor(batch.priority),
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500
                          }}
                        >
                          {batch.priority} Priority
                        </span>
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '4px' }}>
                        {batch.description}
                      </div>
                      <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                        {batch.count} payers
                      </div>
                    </div>
                    <button
                      onClick={() => handleIntegrateBatch(batch.id)}
                      disabled={isProcessing || isCompleted}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: isCompleted ? '#10B981' : '#2563EB',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: isCompleted ? 'default' : 'pointer',
                        opacity: isProcessing && !isCompleted ? 0.6 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {isCompleted ? (
                        <>
                          <span>✓</span>
                          <span>Integrated</span>
                        </>
                      ) : (
                        <span>+ Integrate</span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#F9FAFB'
          }}
        >
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            Integration will add payers to your clinic's payer database
          </div>
          <button
            onClick={allBatchesCompleted ? onComplete : onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {allBatchesCompleted ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
};

export default BatchPayerIntegration;
